/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import { Box, Input, InputGroup, InputRightElement, Text } from '@chakra-ui/react';
import { ChevronDownIcon } from '@chakra-ui/icons';
import { UserTextSelectComponent } from '@karya/core';
import { ChangeEventHandler, InputHTMLAttributes, useState } from 'react';
import { ParsedParams } from '@karya/generic-task-react';
import { ComponentRendererProps } from '../ComponentRenderer';

// Same default message as the Android client
const DEFAULT_ERROR_MESSAGE = 'Entered value is invalid';

// Keyboard hint for each input type, anything else falls back to the full keyboard
const INPUT_MODES: Record<string, InputHTMLAttributes<HTMLInputElement>['inputMode']> = {
  INTEGER: 'numeric',
  FLOAT: 'decimal',
};

// The menu scrolls past this height so a long option list never takes over the screen
const MENU_MAX_HEIGHT = '160px';

export function UserTextSelectRenderer(props: ComponentRendererProps) {
  const typedParams = props.node.params as ParsedParams<UserTextSelectComponent>;
  const component = props.node.component as UserTextSelectComponent;

  const options = typedParams.options ?? [];

  // Typed text is kept locally since only an exact option match is written to the output
  const [text, setText] = useState(typedParams.selected ?? '');
  const [isOpen, setIsOpen] = useState(false);

  // Suggestions are filtered by prefix, ignoring case, same as the Android client
  const filteredOptions = options.filter((option) => option.toLowerCase().startsWith(text.toLowerCase()));

  const writeValue = (value: string) => {
    setText(value);
    // Output stays empty until the typed text matches an option exactly
    props.writeOutput(component.selected, options.includes(value) ? value : '');
  };

  const handleChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    if (!props.isCurrent) {
      return;
    }
    writeValue(e.target.value);
    setIsOpen(true);
  };

  const handlePick = (option: string) => {
    if (!props.isCurrent) {
      return;
    }
    writeValue(option);
    setIsOpen(false);
  };

  if (!props.isCurrent) {
    return (
      <Text fontSize="sm" width="100%">
        {typedParams.selected}
      </Text>
    );
  }

  return (
    <Box width="100%" maxWidth="100%" minWidth={0}>
      <InputGroup size="sm">
        <Input
          bg="white"
          color="gray.800"
          borderColor={isOpen ? 'blue.500' : 'gray.300'}
          borderRadius="6px"
          pr="32px"
          value={text}
          onChange={handleChange}
          // Tapping the field toggles the menu, so it can be reopened after picking an option
          onClick={() => setIsOpen((open) => !open)}
          onBlur={() => setIsOpen(false)}
          placeholder="Choose item"
          _placeholder={{ color: 'blue.700' }}
          autoComplete="off"
          spellCheck={false}
          inputMode={INPUT_MODES[typedParams.type ?? ''] ?? 'text'}
          width="100%"
          maxWidth="100%"
        />
        <InputRightElement
          pointerEvents="none"
          color="blue.600"
          transform={isOpen ? 'rotate(180deg)' : undefined}
          transition="transform 0.15s"
        >
          <ChevronDownIcon boxSize={5} />
        </InputRightElement>
      </InputGroup>

      {isOpen && filteredOptions.length > 0 && (
        <Box
          role="listbox"
          mt="4px"
          bg="white"
          borderWidth="1px"
          borderColor="gray.200"
          borderRadius="6px"
          boxShadow="sm"
          maxH={MENU_MAX_HEIGHT}
          overflowY="auto"
        >
          {filteredOptions.map((option) => {
            const isSelected = option === text;
            return (
              <Box
                key={option}
                role="option"
                aria-selected={isSelected}
                px="10px"
                py="6px"
                fontSize="sm"
                color="gray.700"
                cursor="pointer"
                wordBreak="break-word"
                bg={isSelected ? 'blue.50' : 'white'}
                fontWeight={isSelected ? 'semibold' : 'normal'}
                borderBottomWidth="1px"
                borderBottomColor="gray.200"
                _last={{ borderBottomWidth: 0 }}
                _hover={{ bg: 'gray.100' }}
                // Keep the input focused so the menu does not close before the click lands
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handlePick(option)}
              >
                {option}
              </Box>
            );
          })}
        </Box>
      )}

      {filteredOptions.length === 0 && (
        <Text fontSize="xs" color="red.500" mt="4px">
          {typedParams.error ?? DEFAULT_ERROR_MESSAGE}
        </Text>
      )}
    </Box>
  );
}
